import { Component, Input, Output, ChangeDetectionStrategy, EventEmitter, OnChanges, ViewChild, ElementRef } from '@angular/core';
import * as d3 from 'd3';

export interface ChartLegendItem {
    shape: string;
    color?: string;
    stroke?: string;
    class?: string;
    label: string;
    key: any;
    isSelected?: boolean;
}

@Component({
    selector: 'chart-legend',
    template: require('./chart-legend.component.html'),
    styles: [require('./chart-legend.component.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ChartLegendComponent implements OnChanges {

    @Output()
    public mouseOverItem = new EventEmitter<ChartLegendItem>();

    @Output()
    public mouseOutItem = new EventEmitter<ChartLegendItem>();

    @Output()
    public selectionChanged = new EventEmitter<ChartLegendItem[]>();

    @Input()
    public items: ChartLegendItem[] = [];

    @Input()
    public isVertical = false;

    private shapesDict: { [index: string]: d3.SymbolType } = {
        'circle': d3.symbolCircle,
        'diamond': d3.symbolDiamond,
        'cross': d3.symbolCross,
        'triangle-up': d3.symbolTriangle,
        'triangle-down': d3.symbolTriangle,
        'square': d3.symbolSquare,
        'line': {
            draw: (context: CanvasPath, size: number) => {
                context.rect(-1, -1, 8, 2);
            }
        }
    };

    private isViewInitialized = false;

    @ViewChild('legendDiv')
    private legendDiv: ElementRef;

    public ngOnChanges() {
        if (this.isViewInitialized) {
            this.render();
        }
    }

    public ngAfterViewInit() {
        this.render();
        this.isViewInitialized = true;
    }

    private render() {
        const el = d3.select(this.legendDiv.nativeElement);

        el  .select('svg')
            .selectAll('path')
            .on('mouseover', null)
            .on('mouseout', null)
            .on('click', null);

        el.selectAll('div').remove();

        const enableSelection = this.selectionChanged.observers.length !== 0;

        const divs = el
            .classed('chart-legend--vertical', this.isVertical)
            .selectAll('div')
            .data(this.items || [])
            .enter()
            .append('div')
            .classed('chart-legend__item', true)
            .classed('selectable', enableSelection)
            .on('mouseover', (item: ChartLegendItem) => this.mouseOverItem.emit(item))
            .on('mouseout', (item: ChartLegendItem) => this.mouseOutItem.emit(item))
            .on('click', (item: ChartLegendItem) => {

                if (enableSelection) {
                    item.isSelected = !item.isSelected;
                    el.selectAll<HTMLBaseElement, ChartLegendItem>('.item-title').classed('selected-item', legendItem => legendItem.isSelected);
                    this.selectionChanged.emit(this.items.filter(i => i.isSelected));
                }

            });

        const svgs = divs
            .append('svg')
            .attr('width', '18')
            .attr('height', '20')
            .append('g')
            .attr('transform', 'translate(8, 10)');

        svgs
            .append('path')
            .attr('d', d3.symbol()
                .type((d: ChartLegendItem) =>
                    this.getD3Symbol(d.shape)))
            .attr('transform', d => d.shape === 'triangle-down' ? 'translate(1,-3) rotate(180)' : 'rotate(0)')
            .style('fill', (d: ChartLegendItem) => d.color)
            .style('stroke', (d: ChartLegendItem) => d.stroke || d.color)
            .attr('class', (d: ChartLegendItem) => d.class);


        divs
            .append('div')
            .classed('item-title', true)
            .text(d => d.label);
    }

    private getD3Symbol(shape: string) {
        return this.shapesDict[shape] || d3.symbolCircle;
    }
}